/*
 * SEARCH LAYER — a full-screen search for any theme that gives its markup.
 *
 * All logic is here (typing, requests, sections, prices, links, recently
 * viewed, tracking); the theme gives the in-DOM Vue template:
 *   <div data-search-app
 *        data-delimiters="[[,]]"          optional
 *        data-texts="#json-script-id"     optional, the texts the template reads via t(key)
 *        data-trending="a,b,c"            shown until /api/suggestions answers (and if it gives none)
 *        data-body-class="search-open"    set on <body> while open
 *        data-close-delay="800">          ms the exit animation takes
 *   ... [[ ]] template using the data / computed / methods below ...
 *   </div>
 * State for the markup: isOpen (render it), shown (entrance class), closing (exit class).
 * Openers: [data-search-open] anywhere, or SearchLayer.open(q).
 * After each render "search:rendered" fires on document (for the theme's
 * own animation / wishlist hearts).
 *
 * Endpoints: GET /api/suggestions → {currency, top[], suggestions[], recommended[]} (recommended: the visitor's own,
 *            from the recent-events cookie),
 *            POST /api/search {q, aggregate} → {currency, data[], count}.
 * Recently viewed: recorded on a product page from [data-pdp-wish] (its
 * data-wish-* describe the variant), kept in the browser.
 */
const SearchLayer = (() => {
    const RECENT_KEY = 'vv_recent';
    const RECENT_MAX = 8;
    let app = null;
    let root = null;

    const headers = () => (window.http && typeof http.headers === 'function') ? http.headers() : { 'Content-Type': 'application/json', Accept: 'application/json' };
    const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const readRecent = () => {
        try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
    };
    // a copy, shuffled (the cached API answer keeps its order)
    const shuffle = (list) => {
        const a = (list || []).slice();
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
    };

    const component = (el) => {
        let texts = {};
        try {
            const src = el.dataset.texts ? document.querySelector(el.dataset.texts) : null;
            texts = src ? JSON.parse(src.textContent) : {};
        } catch (e) { texts = {}; }
        const trending = String(el.dataset.trending || '').split(',').map((x) => x.trim()).filter(Boolean);
        return {
            data() {
                return {
                    texts,
                    // isOpen: in the DOM; shown: a frame later, so the entrance transition plays
                    isOpen: false, shown: false, closing: false,
                    q: '', placeholder: texts.placeholder || '',
                    trending,
                    top: [], suggestions: [], recommended: [], currency: null,
                    results: [], count: 0, searched: '', loading: false, error: false,
                    idle: { recent: [], forYou: [], onSale: [] },
                };
            },
            computed: {
                hasQuery() { return this.q.trim().length > 0; },
                hasResults() { return this.results.length > 0; },
            },
            methods: {
                t(key, fallback) { return this.texts[key] ?? fallback ?? ''; },
                open(q) {
                    if (this.isOpen) { if (q) this.searchFor(q); return; }
                    this.isOpen = true; this.closing = false; this.shown = false;
                    setTimeout(() => { this.shown = true; }, 20);
                    if (el.dataset.bodyClass) document.body.classList.add(el.dataset.bodyClass);
                    document.addEventListener('keydown', this.onKey);
                    this.loadSuggestions();
                    this.buildIdle();
                    this.startCycle();
                    this.$nextTick(() => {
                        const input = this.$refs.input;
                        // no scroll jump: the layer is its own scroll container
                        setTimeout(() => { if (input) input.focus({ preventScroll: true }); if (this.$el) this.$el.scrollTop = 0; }, 420);
                        if (q) this.searchFor(q);
                        this.rendered();
                    });
                },
                close() {
                    if (!this.isOpen) return;
                    this.stopCycle();
                    this.closing = true; this.shown = false;
                    if (el.dataset.bodyClass) document.body.classList.remove(el.dataset.bodyClass);
                    document.removeEventListener('keydown', this.onKey);
                    setTimeout(() => { this.isOpen = false; this.closing = false; this.q = ''; this.results = []; this.searched = ''; }, Number(el.dataset.closeDelay) || 0);
                },
                onKey(e) { if (e.key === 'Escape') this.close(); },
                // a click on the layer's own background (not on its content) closes it
                backdrop(e) { if (e.target === e.currentTarget || e.target.hasAttribute('data-search-backdrop')) this.close(); },
                searchFor(term) {
                    this.stopCycle();
                    this.q = term;
                    this.run(term);
                },
                // 200 ms after the last keystroke; an empty field goes straight back to the suggestions
                onInput() {
                    this.stopCycle();
                    clearTimeout(this._debounce);
                    const q = this.q.trim();
                    if (!q) { this.results = []; this.searched = ''; this.rendered(); return; }
                    this._debounce = setTimeout(() => this.run(this.q.trim()), 200);
                },
                submit() { const q = this.q.trim(); if (q) this.run(q); },
                async run(q) {
                    if (q.length < 2) return;
                    if (window.AppEvents) AppEvents.callEvent('SEARCH', { search_string: q });
                    // answers come back out of order: only the latest one counts
                    const seq = this._seq = (this._seq || 0) + 1;
                    this.loading = true; this.error = false;
                    try {
                        const r = await fetch('/api/search', { method: 'POST', headers: headers(), body: JSON.stringify({ q, aggregate: true }) });
                        const d = await r.json();
                        if (seq !== this._seq) return;
                        this.currency = d.currency || this.currency;
                        this.results = (d.data || []).slice(0, Number(el.dataset.max) || 12);
                        this.count = d.count || (d.data || []).length;
                        this.searched = q;
                    } catch (e) {
                        if (seq !== this._seq) return;
                        this.error = true; this.results = []; this.searched = q;
                    }
                    this.loading = false;
                    this.rendered();
                },
                async loadSuggestions() {
                    try {
                        const r = await fetch('/api/suggestions', { headers: headers() });
                        const d = await r.json();
                        this.currency = d.currency || this.currency;
                        this.top = d.top || [];
                        this.suggestions = d.suggestions || [];
                        this.recommended = d.recommended || [];
                        const names = this.top.map((i) => i.product && i.product.name).filter(Boolean).slice(0, 6);
                        if (names.length) this.trending = names;
                    } catch (e) { /* the fallback trending terms stay */ }
                    this.buildIdle();
                    this.rendered();
                },
                // before typing: recently viewed (or suggestions for a first visit) + on sale, a new mix on every open
                buildIdle() {
                    const sug = shuffle(this.suggestions);
                    this.idle = {
                        recent: readRecent().slice(0, RECENT_MAX),
                        // "for you": the visitor's own recommendations, the shuffled suggestions for a first visit
                        forYou: (this.recommended.length ? this.recommended : sug).slice(0, 8),
                        onSale: sug.filter((i) => this.isOnSale(i)).slice(0, 4),
                    };
                },
                // the placeholder cycles through the trending terms (each run has a generation: no late swap after typing)
                startCycle() {
                    if (reduceMotion()) return;
                    const gen = this._gen = (this._gen || 0) + 1;
                    let i = 0;
                    const step = () => {
                        if (gen !== this._gen || this.q || !this.trending.length) return;
                        this.placeholder = this.t('cycling_prefix', '') + ' ' + this.trending[i % this.trending.length];
                        i++;
                    };
                    setTimeout(step, 1400);
                    this._cycle = setInterval(step, 2800);
                },
                stopCycle() {
                    this._gen = (this._gen || 0) + 1;
                    if (this._cycle) { clearInterval(this._cycle); this._cycle = null; }
                    this.placeholder = this.t('placeholder', '');
                },
                rendered() {
                    this.$nextTick(() => document.dispatchEvent(new CustomEvent('search:rendered', { detail: { element: this.$el } })));
                },

                /* ── an item of /api/search or /api/suggestions ── */
                isOnSale(item) { return item.product_discount_prices != null && item.product_discount_prices !== '' && Number(item.product_discount_prices) > 0; },
                itemUrl(item) { return (window.SHOP_BASE || '') + '/product/' + item.product_id + '-' + ((item.product && item.product.slug) || '') + '/' + item.id; },
                itemName(item) { return (item.product && item.product.name) || ''; },
                itemImage(item) { return (item.image && item.image.src) || ''; },
                itemSrcset(item) { return (item.image && item.image.srcset) || ''; },
                itemPrice(item) { return this.money(this.isOnSale(item) ? item.product_discount_prices : item.product_prices); },
                itemOriginal(item) { return this.isOnSale(item) ? this.money(item.product_prices) : ''; },
                money(amount) {
                    if (amount === null || amount === undefined || amount === '') return '';
                    const code = (this.currency && (this.currency.code || this.currency.currency_code)) || (typeof currency === 'string' ? currency : '');
                    if (code) return Money.format(amount, code);
                    const sym = (this.currency && this.currency.symbol) || '';
                    return (Money.format(amount, '') + ' ' + sym).trim();
                },
            },
            beforeUnmount() { this.stopCycle(); },
        };
    };

    const mount = () => {
        if (root) return root;
        const el = document.querySelector('[data-search-app]');
        if (!el || typeof Vue === 'undefined') return null;
        const comp = component(el);
        if (el.dataset.delimiters) comp.delimiters = el.dataset.delimiters.split(',');
        app = Vue.createApp(comp);
        root = app.mount(el);
        return root;
    };

    // a product page: remember the variant for "recently viewed"
    const recordView = () => {
        const btn = document.querySelector('[data-pdp-wish][data-wish-id]');
        if (!btn) return;
        const entry = {
            type: btn.dataset.wishType || 'variant',
            id: btn.dataset.wishId,
            name: btn.dataset.wishTitle || '',
            price: btn.dataset.wishPrice || '',
            image: btn.dataset.wishImage || '',
            url: btn.dataset.wishUrl || (location.pathname + location.search),
        };
        const list = readRecent().filter((p) => p && String(p.id) !== String(entry.id));
        list.unshift(entry);
        try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (e) { /* private window */ }
    };

    document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-search-open]');
        if (!opener) return;
        const r = mount();
        if (!r) return;
        e.preventDefault();
        r.open(opener.dataset.searchOpen || '');
    });

    const boot = () => { recordView(); setTimeout(mount, 0); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

    return {
        open(q) { const r = mount(); if (r) r.open(q || ''); return !!r; },
        close() { if (root) root.close(); },
        available() { return !!document.querySelector('[data-search-app]'); },
        get app() { return root; },
    };
})();

window.SearchLayer = SearchLayer;
